/**
 * Feature-local helpers: id normalization for raw Mongoose docs, parsing of the
 * agent-factory error envelopes (the AppError shape `{error:{code,message,details}}`
 * differs from the stock Nest `{statusCode,message,error}` shape that
 * `@/features/common/apiError` reads), and the fixed `wizard-extra-grants`
 * spec wrapper (design §13.2 — the name is a contract so detail pages can
 * identify wizard-minted grants).
 */
import type {
  AgentFactoryView,
  GrantStatementDraft,
  SubsetDenial,
  TemplatePolicySpec,
} from './types';

/** Templates/memory come back as raw docs (`_id`, no virtual `id`) — tolerate both. */
export function docId(doc: { id?: string; _id?: string } | undefined | null): string {
  return doc?.id ?? doc?._id ?? '';
}

interface AppErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
  statusCode?: number;
  message?: string | string[];
}

function readBody(error: unknown): AppErrorBody | undefined {
  if (error === null || typeof error !== 'object') {
    return undefined;
  }
  const candidate = error as { data?: unknown };
  const body = candidate.data;
  if (body === null || typeof body !== 'object' || body === undefined) {
    return undefined;
  }
  return body as AppErrorBody;
}

const SUBSET_VIOLATION_CODE = 'assigner_lacks_capability';

/**
 * Parse a 403 `assigner_lacks_capability` into the exact denied (action, resource)
 * pairs (`error.details[0].missing[]`). Returns `null` for any other error so
 * callers can fall back to the verbatim message.
 */
export function parseSubsetDenial(error: unknown): SubsetDenial | null {
  const body = readBody(error);
  const nested = body?.error;
  if (!nested || nested.code !== SUBSET_VIOLATION_CODE) {
    return null;
  }

  const firstDetail = Array.isArray(nested.details) ? nested.details[0] : undefined;
  const missing = (firstDetail as { missing?: unknown } | undefined)?.missing;

  const pairs: { action: string; resource: string }[] = [];
  if (Array.isArray(missing)) {
    for (const entry of missing) {
      if (entry && typeof entry === 'object') {
        const grant = entry as { action?: unknown; resource?: unknown };
        if (typeof grant.action === 'string' && typeof grant.resource === 'string') {
          pairs.push({ action: grant.action, resource: grant.resource });
        }
      }
    }
  }

  return { message: nested.message, pairs };
}

/**
 * Human-readable message for any agent-factory failure: AppError bodies carry
 * `error.message`, stock bodies carry `message` — surface the backend text
 * verbatim (T6 guard), with the caller's fallback when there is none.
 */
export function agentErrorMessage(error: unknown, fallback: string): string {
  const body = readBody(error);
  const nestedMessage = body?.error?.message;
  if (typeof nestedMessage === 'string' && nestedMessage.length > 0) {
    return nestedMessage;
  }
  const raw = body?.message;
  const serverMessage = Array.isArray(raw) ? raw.join(' ') : raw;
  if (typeof serverMessage === 'string' && serverMessage.length > 0) {
    return serverMessage;
  }
  return fallback;
}

/** Wrap the wizard's grant rows into the ONE fixed-name spec (design §13.2). */
export function toExtraGrantsSpec(statements: GrantStatementDraft[]): TemplatePolicySpec {
  return {
    name: 'wizard-extra-grants',
    statements: statements.map((statement) => ({
      effect: statement.effect,
      actions: [statement.action],
      resources: [statement.resource],
    })),
  };
}

/** `factory.memory.collectionIds` — the memory view is a loose Record server-side. */
export function memoryCollectionIds(factory: AgentFactoryView | undefined): string[] {
  const raw = (factory?.memory as { collectionIds?: unknown } | undefined)?.collectionIds;
  return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : [];
}
